/**
 * Iter40b — <FlacField> wrapper + useFlacField() hook.
 *
 * Thin helpers to apply FLAC rules to form inputs without per-field boilerplate.
 *
 * USAGE (wrapper):
 *   <FlacField name="cnp" entity="client" flac={flac}>
 *     <Input value={form.cnp} onChange={update('cnp')} />
 *   </FlacField>
 *
 *   - If field is HIDDEN  → the wrapper returns null (don't render child or label)
 *   - If field is READ-ONLY → injects `disabled=true`, title tooltip and
 *                              `className` adornment on the child via cloneElement
 *   - If field is WRITE   → passthrough (renders child unchanged)
 *
 * USAGE (hook, for complex children — Selects, custom layouts, multi-input rows):
 *   const { hidden, disabled, props } = useFlacField('cnp', 'client', flac);
 *   if (hidden) return null;
 *   return <Select disabled={disabled} {...props} />;
 *
 * NOTES:
 *   - `name` must match the Pydantic body field name on the backend (leaf).
 *   - `entity` is one of "sale" | "client" | "contract" | "contract_status".
 *   - The wrapper is intentionally narrow: only handles a SINGLE child input.
 *     For groups / sections that should hide as a unit, wrap the parent block
 *     manually using `flac.canWrite('client.cnp')` etc.
 */
import { cloneElement, isValidElement } from "react";

export function useFlacField(name, entity, flac) {
  if (!flac || !flac.enforced) {
    return { hidden: false, disabled: false, props: {} };
  }
  const key = `${entity}.${name}`;
  if (flac.isHidden && flac.isHidden(key)) {
    return { hidden: true, disabled: true, props: {} };
  }
  if (flac.isReadOnly && flac.isReadOnly(key)) {
    return {
      hidden: false,
      disabled: true,
      props: {
        disabled: true,
        title: `Read-only pentru rolul „${flac.role || "—"}".`,
        "data-flac-readonly": true,
        className: "bg-slate-50 cursor-not-allowed",
      },
    };
  }
  return { hidden: false, disabled: false, props: {} };
}

export default function FlacField({
  name,
  entity = "sale",
  flac,
  children,
  hideWrapper = true, // if true, return null when hidden; if false, render disabled placeholder
}) {
  const { hidden, props } = useFlacField(name, entity, flac);

  if (hidden) {
    if (hideWrapper) return null;
    return (
      <div className="text-xs italic text-slate-400" data-flac-hidden={true}>
        — ascuns pentru rolul „{flac?.role || "—"}"
      </div>
    );
  }

  if (!isValidElement(children)) return children;

  if (!Object.keys(props).length) return children;

  // Merge classNames carefully
  const merged = {
    ...props,
    className: [children.props.className, props.className].filter(Boolean).join(" "),
  };
  return cloneElement(children, merged);
}
